import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { OfflineBanner } from "@/components/OfflineBanner";
import { InventoryPendingSync } from "@/components/InventoryPendingSync";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Pages publiques (non protégées) - imports statiques
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import PasswordResetEmailSent from "./pages/PasswordResetEmailSent";
import NotFound from "./pages/NotFound";

// Pages protégées - lazy loading pour sécuriser le token webhook
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Clients = lazy(() => import("./pages/Clients"));
const ClientDetail = lazy(() => import("./pages/ClientDetail"));
const NewClient = lazy(() => import("./pages/NewClient"));
const NewPurchase = lazy(() => import("./pages/NewPurchase"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const NewOrder = lazy(() => import("./pages/NewOrder"));
const DryGoodsOrders = lazy(() => import("./pages/DryGoodsOrders"));
const NewDryGoodsOrder = lazy(() => import("./pages/NewDryGoodsOrder"));
const DryGoodsOrderDetail = lazy(() => import("./pages/DryGoodsOrderDetail"));
const DryGoodsConfig = lazy(() => import("./pages/DryGoodsConfig"));
const DryGoodsManagement = lazy(() => import("./pages/DryGoodsManagement"));
const DryGoodsManagementDetail = lazy(() => import("./pages/DryGoodsManagementDetail"));
const Temperatures = lazy(() => import("./pages/Temperatures"));
const EquipmentConfiguration = lazy(() => import("./pages/EquipmentConfiguration"));
const TemperatureEntry = lazy(() => import("./pages/TemperatureEntry"));
const WeeklyReport = lazy(() => import("./pages/WeeklyReport"));
const Settings = lazy(() => import("./pages/Settings"));
const QualityReports = lazy(() => import("./pages/QualityReports"));
const NewQualityReport = lazy(() => import("./pages/NewQualityReport"));
const QualityReportDetail = lazy(() => import("./pages/QualityReportDetail"));
const CashDeposits = lazy(() => import("./pages/CashDeposits"));
const NewCashDeposit = lazy(() => import("./pages/NewCashDeposit"));
const CashDepositDetail = lazy(() => import("./pages/CashDepositDetail"));
const GoogleSchedules = lazy(() => import("./pages/GoogleSchedules"));
const GoogleReviews = lazy(() => import("./pages/GoogleReviews"));
const GoogleReviewStats = lazy(() => import("./pages/GoogleReviewStats"));
const SocialContents = lazy(() => import("./pages/SocialContents"));
const NewSocialContent = lazy(() => import("./pages/NewSocialContent"));
const SocialContentDetail = lazy(() => import("./pages/SocialContentDetail"));
const SocialContentManagement = lazy(() => import("./pages/SocialContentManagement"));
const SocialContentManagementDetail = lazy(() => import("./pages/SocialContentManagementDetail"));
const ScanVitrine = lazy(() => import("./pages/ScanVitrine"));
const ScanVitrineHistorique = lazy(() => import("./pages/ScanVitrineHistorique"));
const GestionEans = lazy(() => import("./pages/GestionEans"));
const Inventories = lazy(() => import("./pages/Inventories"));
const InventoryConfig = lazy(() => import("./pages/InventoryConfig"));
const InventoryEntry = lazy(() => import("./pages/InventoryEntry"));
const InventorySummary = lazy(() => import("./pages/InventorySummary"));
const InventoryHistory = lazy(() => import("./pages/InventoryHistory"));
const BuchesComptoir = lazy(() => import("./pages/buches/BuchesComptoir"));
const BuchesPilotage = lazy(() => import("./pages/buches/BuchesPilotage"));
const BuchesReservations = lazy(() => import("./pages/buches/BuchesReservations"));
const BucheReservationForm = lazy(() => import("./pages/buches/BucheReservationForm"));
const BucheReservationDetail = lazy(() => import("./pages/buches/BucheReservationDetail"));
const BucheRemise = lazy(() => import("./pages/buches/BucheRemise"));
const BucheInventaire = lazy(() => import("./pages/buches/BucheInventaire"));
const BuchesStock = lazy(() => import("./pages/buches/BuchesStock"));
const BuchesFacturation = lazy(() => import("./pages/buches/BuchesFacturation"));
const BuchesConfig = lazy(() => import("./pages/buches/BuchesConfig"));

// Redirect component for /cash-deposits/:id → /justificatifs/:id
const CashDepositRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/justificatifs/${id}`} replace />;
};

// Configure React Query with network resilience
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry failed queries 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Use cache even when offline, then refetch when back online
      networkMode: 'offlineFirst',

      // Keep data fresh for 5 minutes by default
      staleTime: 5 * 60 * 1000,

      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Refetch on window focus to keep data fresh
      refetchOnWindowFocus: true,

      // Refetch automatically when network reconnects (critical for offline navigation)
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry failed mutations 2 times (fewer than queries to avoid duplicates)
      retry: 2,

      // Fixed 1 second delay between retries for mutations
      retryDelay: 1000,

      // Use cache even when offline
      networkMode: 'offlineFirst',
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <OrganizationProvider>
        <InventoryPendingSync />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <OfflineBanner />
          <ScrollToTop />
          <ErrorBoundary>
            <Suspense
              fallback={
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                    <p className="text-gray-500 mt-4">Chargement...</p>
                  </div>
                </div>
              }
            >
            <Routes>
            {/* Routes publiques */}
            <Route path="/welcome" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/password-reset-email-sent" element={<PasswordResetEmailSent />} />

            {/* Routes protégées (lazy loaded) */}
            <Route path="/" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/clients/:id" element={<ClientDetail />} />
            <Route path="/clients/new" element={<NewClient />} />
            <Route path="/clients/:id/purchase" element={<NewPurchase />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/new" element={<NewOrder />} />
            <Route path="/orders/:id" element={<OrderDetail />} />
            <Route path="/dry-goods" element={<DryGoodsOrders />} />
            <Route path="/dry-goods/new" element={<NewDryGoodsOrder />} />
            <Route path="/dry-goods/:id" element={<DryGoodsOrderDetail />} />
            <Route path="/dry-goods-config" element={<DryGoodsConfig />} />
            <Route path="/dry-goods-management" element={<DryGoodsManagement />} />
            <Route path="/dry-goods-management/:id" element={<DryGoodsManagementDetail />} />
            <Route path="/temperatures" element={<Temperatures />} />
            <Route path="/temperatures/equipment" element={<EquipmentConfiguration />} />
            <Route path="/temperatures/new" element={<TemperatureEntry />} />
            <Route path="/temperatures/weekly" element={<WeeklyReport />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/quality-reports" element={<QualityReports />} />
            <Route path="/quality-reports/new" element={<NewQualityReport />} />
            <Route path="/quality-reports/:id" element={<QualityReportDetail />} />
            <Route path="/justificatifs" element={<CashDeposits />} />
            <Route path="/justificatifs/new" element={<NewCashDeposit />} />
            <Route path="/justificatifs/:id" element={<CashDepositDetail />} />
            {/* Redirects for backwards compatibility */}
            <Route path="/cash-deposits" element={<Navigate to="/justificatifs" replace />} />
            <Route path="/cash-deposits/new" element={<Navigate to="/justificatifs/new" replace />} />
            <Route path="/cash-deposits/:id" element={<CashDepositRedirect />} />
            <Route path="/google-schedules" element={<GoogleSchedules />} />
            <Route path="/avis-google" element={<GoogleReviews />} />
            <Route path="/avis-google/statistiques" element={<GoogleReviewStats />} />
            <Route path="/social-content" element={<SocialContents />} />
            <Route path="/social-content/new" element={<NewSocialContent />} />
            <Route path="/social-content/:id" element={<SocialContentDetail />} />
            <Route path="/social-content-management" element={<SocialContentManagement />} />
            <Route path="/social-content-management/:id" element={<SocialContentManagementDetail />} />
            <Route path="/inventaires" element={<Inventories />} />
            <Route path="/inventaires/config" element={<InventoryConfig />} />
            <Route path="/inventaires/historique" element={<InventoryHistory />} />
            <Route path="/inventaires/:id" element={<InventoryEntry />} />
            <Route path="/inventaires/:id/synthese" element={<InventorySummary />} />
            <Route path="/buches" element={<BuchesComptoir />} />
            <Route path="/buches/pilotage" element={<BuchesPilotage />} />
            <Route path="/buches/reservations" element={<BuchesReservations />} />
            <Route path="/buches/reservations/nouvelle" element={<BucheReservationForm />} />
            <Route path="/buches/reservations/:id" element={<BucheReservationDetail />} />
            <Route path="/buches/reservations/:id/modifier" element={<BucheReservationForm />} />
            <Route path="/buches/reservations/:id/remise" element={<BucheRemise />} />
            <Route path="/buches/vente" element={<BucheRemise />} />
            <Route path="/buches/inventaire" element={<BucheInventaire />} />
            <Route path="/buches/stock" element={<BuchesStock />} />
            <Route path="/buches/facturation" element={<BuchesFacturation />} />
            <Route path="/buches/config" element={<BuchesConfig />} />

            {/* POC suivi des bacs en vitrine */}
            <Route path="/vitrine-scan" element={<ScanVitrine />} />
            <Route path="/vitrine" element={<ScanVitrineHistorique />} />
            <Route path="/gestion-eans" element={<GestionEans />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
          </ErrorBoundary>
      </BrowserRouter>
      </OrganizationProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
